import React, { useEffect, useMemo, useState } from "react";

import type { ProcessedBetEntry } from "@/hooks/firestore/betting/useEntries";
import type { CashOutProps } from "../../types/entries";

import { usePermission } from "@/features/auth/hooks/usePermission";
import {
  Button,
  CashOutIcon,
  IconButton,
  InfoIcon,
  RefreshIcon,
  Tooltip,
  TrashIcon,
} from "@picklebet/ui";
import { constructCurrency } from "common/currency";
import { useMinimumDelay } from "@/hooks/ui/useMinimumDelay";
import { useConfirmCashOut } from "../../hooks/useConfirmCashOut";
import { triggerHaptic } from "@/utilities/haptics";

import * as styles from "./BetEntryCashOut.module.scss";

interface BetEntryCashOutProps {
  entry: ProcessedBetEntry;
  cashOut?: CashOutProps;
  isCashOutSuspended?: boolean;
}

const BetEntryCashOut = ({
  entry,
  cashOut,
  isCashOutSuspended,
}: BetEntryCashOutProps) => {
  const cashoutPermission = usePermission("cashoutBet");
  const hasCashoutPermission = cashoutPermission === "GRANTED";

  const eligibleForCashOut = entry.data.eligibleForPotentialCashout;

  if (!hasCashoutPermission || !eligibleForCashOut) {
    return null;
  }

  const { confirmCashOut, isCashingOut } = useConfirmCashOut();

  const [cashoutSuccessful, setCashoutSuccessful] = useState(false);

  const showLoading = useMinimumDelay(!!isCashingOut || !!cashOut?.loading);

  const [isConfirming, setIsConfirming] = useState(false);

  const selectionResults = useMemo(
    () => entry.data.selections.map((selection) => selection.result),
    [entry.data.selections],
  );

  useEffect(() => {
    cashOut?.refresh();
  }, [selectionResults]);

  if (isCashOutSuspended) {
    return (
      <div className={styles.cashoutSuspended}>
        <div className={styles.suspendedMessage}>
          <div className={styles.icon}>
            <CashOutIcon />
          </div>
          Cash Out Suspended
          <Tooltip
            contentClassName={styles.tooltipContent}
            arrowClassName={styles.arrowTooltip}
            trigger={
              <div className={styles.infoIconContainer}>
                <InfoIcon className={styles.infoIcon} />
              </div>
            }
            content={
              <div className={styles.tooltipContent}>
                Unavailable while a leg is Live
              </div>
            }
          />
        </div>
      </div>
    );
  }

  if (!cashOut?.amount || !entry.referenceId) return null;

  const onClickCashout = async () => {
    triggerHaptic.click();

    if (isConfirming) {
      const res = await confirmCashOut(entry.referenceId, cashOut.amount);
      setCashoutSuccessful(res);

      // If cashout fails, get new amount and
      // keep in the confirmation state
      if (!res) {
        cashOut?.refresh?.();
        return;
      }

      triggerHaptic.success();

      setIsConfirming(false);
    } else {
      setIsConfirming(true);
    }
  };

  const onClickCancel = () => {
    triggerHaptic.error();
    setIsConfirming(false);
  };

  if (cashoutSuccessful) {
    return (
      <div className={styles.cashoutSuccess}>
        <div className={styles.successMessage}>
          <div className={styles.icon}>
            <CashOutIcon />
          </div>
          Bet Cashed Out
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cashout}>
      <Button
        variant={isConfirming && !showLoading ? "warning" : "info"}
        className={styles.button}
        onClick={onClickCashout}
        loading={showLoading}
      >
        <div className={styles.icon}>
          <CashOutIcon />
        </div>
        {isConfirming ? "Confirm" : "Cash Out"}
        <span className={styles.pill}>
          {constructCurrency(cashOut?.amount)}
        </span>
      </Button>

      {isConfirming ? (
        <IconButton
          variant="blurred"
          className={styles.secondaryButton}
          onClick={onClickCancel}
        >
          <TrashIcon className={styles.trash} />
        </IconButton>
      ) : (
        <IconButton
          variant="blurred"
          className={styles.secondaryButton}
          onClick={
            showLoading
              ? undefined
              : () => {
                  cashOut?.refresh?.();
                  triggerHaptic.click();
                }
          }
        >
          <RefreshIcon className={styles.refresh} />
        </IconButton>
      )}
    </div>
  );
};

export { BetEntryCashOut };
